import React, { useState } from "react";
import {
  FileText,
  Users,
  Activity,
  Search,
  Filter,
  CheckCircle2,
  Heart,
  Brain,
  Eye,
  Ear,
} from "lucide-react";

import { useConsultationWizardState } from "../../../hooks/useConsultationWizardState";

/* eslint-disable @typescript-eslint/no-empty-object-type */
interface FamilyHistoryStepProps {
  // Add properties here if needed
}
/* eslint-enable @typescript-eslint/no-empty-object-type */

const CheckboxField: React.FC<{
  label: string;
  checked: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  category: string;
  icon?: React.FC<React.SVGProps<SVGSVGElement>>;
}> = ({ label, checked, onChange, category, icon: Icon }) => {
  const getCategoryColor = (cat: string) => {
    switch (cat) {
      case "cardiovascular":
        return "from-red-50 to-rose-50 border-red-100";
      case "endocrine":
        return "from-purple-50 to-violet-50 border-purple-100";
      case "respiratory":
        return "from-blue-50 to-cyan-50 border-blue-100";
      case "neurological":
        return "from-green-50 to-emerald-50 border-green-100";
      case "genetic":
        return "from-amber-50 to-orange-50 border-amber-100";
      case "sensory":
        return "from-indigo-50 to-blue-50 border-indigo-100";
      case "digestive":
        return "from-teal-50 to-cyan-50 border-teal-100";
      case "psychological":
        return "from-pink-50 to-purple-50 border-pink-100";
      case "general":
        return "from-gray-50 to-slate-50 border-gray-100";
      default:
        return "from-blue-50 to-indigo-50 border-blue-100";
    }
  };

  return (
    <label
      className={`group flex items-center space-x-3 p-4 rounded-xl border-2 bg-gradient-to-br ${getCategoryColor(
        category
      )} cursor-pointer transition-all duration-300 hover:shadow-md hover:scale-[1.02] ${checked ? "ring-2 ring-blue-400 shadow-lg" : ""
        }`}
    >
      <div className="relative">
        <input type="checkbox" checked={checked} onChange={onChange} className="sr-only" />
        <div
          className={`w-5 h-5 rounded-lg border-2 transition-all duration-200 flex items-center justify-center ${checked
            ? "bg-gradient-to-br from-blue-500 to-blue-600 border-blue-600"
            : "border-gray-300 bg-white group-hover:border-blue-400"
            }`}
        >
          {checked && (
            <CheckCircle2 className="w-3 h-3 text-white animate-in zoom-in-50 duration-200" />
          )}
        </div>
      </div>
      <div className="flex items-center flex-1 min-w-0">
        {Icon && (
          <div className="mr-2 opacity-60">
            <Icon width={16} height={16} />
          </div>
        )}
        <span
          className={`text-sm font-medium transition-colors duration-200 truncate ${checked ? "text-blue-700" : "text-gray-700 group-hover:text-gray-900"
            }`}
        >
          {label}
        </span>
      </div>
    </label>
  );
};

const FamilyHistoryStep: React.FC<FamilyHistoryStepProps> = () => {
  const { formData, updateFormData } = useConsultationWizardState();
  const [familySearchTerm, setFamilySearchTerm] = useState("");
  const [systemsSearchTerm, setSystemsSearchTerm] = useState("");
  const [familyCategory, setFamilyCategory] = useState("all");
  const [systemsCategory, setSystemsCategory] = useState("all");

  const familyConditions = [
    { key: "hypertensionArterielle", label: "Hypertension artérielle", category: "cardiovascular", icon: Heart },
    { key: "maladieCardiaque", label: "Maladie cardiaque", category: "cardiovascular", icon: Heart },
    { key: "cholesterolEleve", label: "Taux de cholestérol élevé", category: "cardiovascular", icon: Heart },
    { key: "avc", label: "AVC", category: "neurological", icon: Brain },
    { key: "diabete", label: "Diabète", category: "endocrine" },
    { key: "maladieThyroidienne", label: "Maladie thyroïdienne", category: "endocrine" },
    { key: "drepanocytose", label: "Drépanocytose", category: "genetic" },
    { key: "asthme", label: "Asthme", category: "respiratory" },
    { key: "arthrite", label: "Arthrite", category: "general" },
    { key: "cancer", label: "Cancer", category: "general" },
    { key: "retardMental", label: "Retard Mental", category: "neurological", icon: Brain },
    { key: "defautGenetique", label: "Défaut génétique", category: "genetic" },
    { key: "sinusite", label: "Sinusite", category: "respiratory" },
    { key: "vertige", label: "Vertige", category: "neurological", icon: Brain },
  ];

  const systemsObservations = [
    { key: "fatigue", label: "Fatigue / faiblesse", category: "general" },
    { key: "malNutrition", label: "Mal nutrition", category: "general" },
    { key: "pertePoids", label: "Perte ou gain de poids", category: "general" },
    { key: "fievresRecurrentes", label: "Fièvres récurrentes", category: "general" },
    { key: "difficulteMarcher", label: "Dolent / difficulté à marcher", category: "neurological" },
    { key: "hyperactivite", label: "L'hyperactivité", category: "psychological", icon: Brain },
    { key: "ecoulementOculaire", label: "Écoulement oculaire", category: "sensory", icon: Eye },
    { key: "infectionsOreilleChroniques", label: "Infections d'oreille chroniques", category: "sensory", icon: Ear },
    { key: "sangUrine", label: "Sang dans l'urine", category: "general" },
    { key: "myopie", label: "Myopie / dépression", category: "sensory", icon: Eye },
    { key: "douleurAbdominale", label: "Douleur abdominale", category: "digestive" },
    { key: "problemesAudition", label: "Problèmes d'audition", category: "sensory", icon: Ear },
    { key: "mauxGorge", label: "Maux de gorge fréquents", category: "respiratory" },
    { key: "vertiges", label: "Vertiges", category: "neurological", icon: Brain },
    { key: "anxiete", label: "Anxiété", category: "psychological", icon: Brain },
    { key: "ganglionsLymphatiques", label: "Ganglions lymphatiques élargis", category: "general" },
    { key: "congestionChronique", label: "Congestion chronique", category: "respiratory" },
    { key: "insomnie", label: "Insomnie", category: "psychological", icon: Brain },
    { key: "sangMucus", label: "Sang ou mucus dans les selles", category: "digestive" },
    { key: "ecoulementNasal", label: "Écoulement nasal", category: "respiratory" },
    { key: "anemie", label: "Anémie", category: "cardiovascular", icon: Heart },
    { key: "douleurPoitrine", label: "Douleur de poitrine", category: "cardiovascular", icon: Heart },
    { key: "essoufflement", label: "Essoufflement", category: "respiratory" },
    { key: "nauseesVomissements", label: "Nausées ou vomissements", category: "digestive" },
    { key: "diarrheeConstipation", label: "Diarrhée ou constipation", category: "digestive" },
    { key: "douleursMusculaires", label: "Douleurs musculaires", category: "general" },
  ];

  const categories = [
    { key: "all", label: "Toutes", icon: Activity, color: "text-gray-600" },
    { key: "cardiovascular", label: "Cardiovasculaire", icon: Heart, color: "text-red-500" },
    { key: "respiratory", label: "Respiratoire", icon: Activity, color: "text-blue-500" },
    { key: "neurological", label: "Neurologique", icon: Brain, color: "text-green-500" },
    { key: "endocrine", label: "Endocrinien", icon: Activity, color: "text-purple-500" },
    { key: "genetic", label: "Génétique", icon: Activity, color: "text-amber-500" },
    { key: "sensory", label: "Sensoriel", icon: Eye, color: "text-indigo-500" },
    { key: "digestive", label: "Digestif", icon: Activity, color: "text-teal-500" },
    { key: "psychological", label: "Psychologique", icon: Brain, color: "text-pink-500" },
    { key: "general", label: "Général", icon: Activity, color: "text-gray-500" },
  ];

  const filteredFamilyConditions = familyConditions.filter(condition => {
    const matchesSearch = condition.label.toLowerCase().includes(familySearchTerm.toLowerCase());
    const matchesCategory = familyCategory === "all" || condition.category === familyCategory;
    return matchesSearch && matchesCategory;
  });

  const filteredSystemsObservations = systemsObservations.filter(observation => {
    const matchesSearch = observation.label.toLowerCase().includes(systemsSearchTerm.toLowerCase());
    const matchesCategory = systemsCategory === "all" || observation.category === systemsCategory;
    return matchesSearch && matchesCategory;
  });

  const familySelectedCount = familyConditions.filter(condition =>
    formData.antecedentsFamiliaux[condition.key]
  ).length;

  const systemsSelectedCount = systemsObservations.filter(observation =>
    formData.observationsSystemes[observation.key]
  ).length;

  return (
    <div className="space-y-10">
      {/* Particules animées */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        {[...Array(12)].map((_, i) => {
          const icons = [Users, Heart, Brain, Eye, Ear, Activity];
          const Icon = icons[i % icons.length];
          const colors = ['text-red-100', 'text-blue-100', 'text-green-100', 'text-purple-100', 'text-amber-100', 'text-indigo-100'];

          return (
            <div
              key={i}
              className={`absolute ${colors[i % colors.length]} opacity-30 animate-float`}
            >
              <Icon className="w-5 h-5" />
            </div>
          );
        })}
      </div>

      {/* Section Histoire de Famille */}
      <div className="bg-gradient-to-br from-red-50 via-rose-50 to-pink-50 rounded-3xl p-8 shadow-2xl border border-red-100 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-red-400 via-rose-400 to-pink-400 rounded-t-3xl"></div>

        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div className="flex items-center mb-4 md:mb-0">
            <div className="bg-gradient-to-br from-red-100 to-rose-100 p-3 rounded-2xl mr-4">
              <Users className="text-red-600" size={28} />
            </div>
            <div>
              <h3 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-red-600 to-rose-600 bg-clip-text text-transparent">
                Histoire de Famille
              </h3>
              <p className="text-gray-600 font-medium">
                Antécédents médicaux familiaux
              </p>
            </div>
          </div>

          <div className="bg-white/80 backdrop-blur-sm px-4 py-2 rounded-xl border border-white/50 shadow-lg">
            <span className="text-sm text-gray-600 font-medium">Sélectionnées: </span>
            <span className="text-lg font-bold text-red-600">{familySelectedCount}</span>
          </div>
        </div>

        {/* Recherche et filtres famille */}
        <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-white/50 mb-8">
          <div className="flex flex-col md:flex-row gap-4 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Rechercher un antécédent familial..."
                value={familySearchTerm}
                onChange={(e) => setFamilySearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
              />
            </div>
            <div className="flex items-center space-x-2">
              <Filter className="text-gray-500" size={20} />
              <select
                value={familyCategory}
                onChange={(e) => setFamilyCategory(e.target.value)}
                className="px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200 bg-white"
              >
                {categories.map(category => (
                  <option key={category.key} value={category.key}>
                    {category.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {categories.slice(0, 6).map((category) => {
              const Icon = category.icon;
              const isSelected = familyCategory === category.key;

              return (
                <button
                  key={category.key}
                  onClick={() => setFamilyCategory(category.key)}
                  className={`flex items-center px-3 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${isSelected
                    ? 'bg-red-500 text-white shadow-lg scale-105'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                >
                  <Icon className="mr-1" size={14} />
                  {category.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="bg-white/60 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-white/40 mb-6">
          <p className="text-gray-700 mb-6 font-medium flex items-center">
            <FileText className="mr-2 text-red-600" size={18} />
            Si un membre de la famille souffre de l'une des conditions suivantes, veuillez la cocher :
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredFamilyConditions.map((condition) => (
              <div
                key={condition.key}
                className="animate-in slide-in-from-bottom-4 duration-300"
              >
                <CheckboxField
                  label={condition.label}
                  checked={!!formData.antecedentsFamiliaux[condition.key]}
                  onChange={(e) =>
                    updateFormData(
                      "antecedentsFamiliaux",
                      condition.key,
                      e.target.checked
                    )
                  }
                  category={condition.category}
                  icon={condition.icon}
                />
              </div>
            ))}
          </div>

          {filteredFamilyConditions.length === 0 && (
            <div className="text-center py-12">
              <div className="text-gray-400 mb-4">
                <Search size={48} />
              </div>
              <p className="text-gray-500 font-medium">Aucun antécédent trouvé</p>
              <p className="text-gray-400 text-sm">Essayez un autre terme de recherche</p>
            </div>
          )}
        </div>

        <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-6 rounded-2xl shadow-lg border border-amber-100">
          <label className="block text-lg font-semibold text-amber-800 mb-4">
            Autres antécédents familiaux (à préciser)
          </label>
          <textarea
            value={
              typeof formData.antecedentsFamiliaux.autre === "string"
                ? formData.antecedentsFamiliaux.autre
                : ""
            }
            onChange={e =>
              updateFormData("antecedentsFamiliaux", "autre", e.target.value)
            }
            className="w-full px-4 py-3 border-2 border-amber-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all duration-200 resize-none bg-white/80 backdrop-blur-sm"
            rows={3}
            placeholder="Saisir tout autre antécédent familial non listé ci-dessus..."
          />
        </div>
      </div>

      {/* Section Examen des Systèmes */}
      <div className="bg-gradient-to-br from-indigo-50 via-blue-50 to-cyan-50 rounded-3xl p-8 shadow-2xl border border-indigo-100 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-indigo-400 via-blue-400 to-cyan-400 rounded-t-3xl"></div>

        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div className="flex items-center mb-4 md:mb-0">
            <div className="bg-gradient-to-br from-indigo-100 to-blue-100 p-3 rounded-2xl mr-4">
              <Activity className="text-indigo-600" size={28} />
            </div>
            <div>
              <h3 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-indigo-600 to-blue-600 bg-clip-text text-transparent">
                Examen des Systèmes
              </h3>
              <p className="text-gray-600 font-medium">
                Symptômes et observations actuelles
              </p>
            </div>
          </div>

          <div className="bg-white/80 backdrop-blur-sm px-4 py-2 rounded-xl border border-white/50 shadow-lg">
            <span className="text-sm text-gray-600 font-medium">Sélectionnées: </span>
            <span className="text-lg font-bold text-indigo-600">{systemsSelectedCount}</span>
          </div>
        </div>

        {/* Recherche et filtres systèmes */}
        <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-white/50 mb-8">
          <div className="flex flex-col md:flex-row gap-4 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Rechercher un symptôme ou observation..."
                value={systemsSearchTerm}
                onChange={(e) => setSystemsSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200"
              />
            </div>
            <div className="flex items-center space-x-2">
              <Filter className="text-gray-500" size={20} />
              <select
                value={systemsCategory}
                onChange={(e) => setSystemsCategory(e.target.value)}
                className="px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200 bg-white"
              >
                {categories.map(category => (
                  <option key={category.key} value={category.key}>
                    {category.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="bg-white/60 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-white/40 mb-6">
          <p className="text-gray-700 mb-6 font-medium">
            Veuillez cocher les éléments suivants qui constituent un problème courant pour le patient :
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredSystemsObservations.map((observation) => (
              <div
                key={observation.key}
                className="animate-in slide-in-from-bottom-4 duration-300"
              >
                <CheckboxField
                  label={observation.label}
                  checked={!!formData.observationsSystemes[observation.key]}
                  onChange={(e) =>
                    updateFormData(
                      "observationsSystemes",
                      observation.key,
                      e.target.checked
                    )
                  }
                  category={observation.category}
                  icon={observation.icon}
                />
              </div>
            ))}
          </div>

          {filteredSystemsObservations.length === 0 && (
            <div className="text-center py-12">
              <div className="text-gray-400 mb-4">
                <Search size={48} />
              </div>
              <p className="text-gray-500 font-medium">Aucune observation trouvée</p>
              <p className="text-gray-400 text-sm">Essayez un autre terme de recherche</p>
            </div>
          )}
        </div>
        {/* ✅ Ajout: Champ Durée des Symptômes */}
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-4 rounded-xl border border-blue-100 mb-6">
          <label className="block text-sm font-semibold text-blue-800 mb-2">
            ⏱️ Durée des symptômes
          </label>
          <input
            type="text"
            value={formData.patientInfo?.dureeSymptomes || ""}
            onChange={(e) =>
              updateFormData("patientInfo", "dureeSymptomes", e.target.value)
            }
            className="w-full px-4 py-3 border-2 border-blue-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white/80 backdrop-blur-sm"
            placeholder="Ex: 2 jours, 1 semaine, depuis 1 mois..."
          />
          <p className="text-xs text-blue-600 mt-2">
            Précisez depuis combien de temps le patient présente ces symptômes
          </p>
        </div>
        <div className="bg-gradient-to-br from-teal-50 to-cyan-50 p-6 rounded-2xl shadow-lg border border-teal-100">
          <label className="block text-lg font-semibold text-teal-800 mb-4">
            Autres observations systèmes (à préciser)
          </label>
          <textarea
            value={
              typeof formData.observationsSystemes.autre === "string"
                ? formData.observationsSystemes.autre
                : ""
            }
            onChange={e =>
              updateFormData("observationsSystemes", "autre", e.target.value)
            }
            className="w-full px-4 py-3 border-2 border-teal-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all duration-200 resize-none bg-white/80 backdrop-blur-sm"
            rows={3}
            placeholder="Saisir toute autre observation non listée ci-dessus..."
          />
        </div>
      </div>
    </div>
  );
};

export default FamilyHistoryStep;